import { createAsyncThunk } from '@reduxjs/toolkit';

import type RootState from 'app-components/utils/redux-store/rootState';

import { getLogger } from '../../utils/core';
import { bindPromiseAction } from '../core/actionHelpers';
import { getTransaction } from '../transactions/transactionsActions';
import { addUndo } from '../undo/undoActions';
import { mkUndo } from '../undo/undoTypes';
import { getReceiptSessionEpoch } from './receiptSessionEpoch';
import { receiptsActions } from './receiptsSlice';
import { RECEIPTS_REDUCER_KEY } from './receiptsTypes';

const log = getLogger('flux/receipts/editorReceiptChanges');

export type ReceiptSession = { sessionEpoch: number; storeEpoch: number };

/** Read when Update starts: a logout or dataset switch during the save must not apply its receipt changes. */
export const captureReceiptSession =
  () =>
  (dispatch: object, getState: () => unknown): ReceiptSession => ({
    sessionEpoch: (getState() as RootState)[RECEIPTS_REDUCER_KEY].sessionEpoch,
    storeEpoch: getReceiptSessionEpoch(dispatch),
  });

export type EditorReceiptChanges = {
  transactionId: string;
  session: ReceiptSession;
  linkReceiptId?: string;
  unlinkReceiptId?: string;
  deleteReceiptId?: string;
};

/** The receipts service writes the attachment itself, so the transaction is refetched after a detach or delete. */
export const applyEditorReceiptChanges = createAsyncThunk(
  'receipts/applyEditorReceiptChanges',
  async (
    { transactionId, session, linkReceiptId, unlinkReceiptId, deleteReceiptId }: EditorReceiptChanges,
    thunkApi,
  ) => {
    const isCurrentSession = (): boolean =>
      (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].sessionEpoch === session.sessionEpoch &&
      getReceiptSessionEpoch(thunkApi.dispatch) === session.storeEpoch;
    // Errors from an ended session aren't reported in the next one.
    const showError = (userMessage: string): void => {
      if (!isCurrentSession()) return;
      thunkApi.dispatch(addUndo(mkUndo({ disableUndoAction: true, userMessage })));
    };
    let detached = false;

    if (linkReceiptId && isCurrentSession()) {
      try {
        await thunkApi
          .dispatch(receiptsActions.linkTargetedReceiptToTransaction({ id: linkReceiptId, transactionId }))
          .unwrap();
      } catch (error) {
        log.error('Failed to attach the staged receipt:', error);
        // The receipt keeps its target so the editor can retry.
        showError("The receipt couldn't be attached. Open the transaction to try again.");
      }
    }

    if (unlinkReceiptId && isCurrentSession()) {
      try {
        await thunkApi.dispatch(receiptsActions.unlinkReceiptFromTransaction(unlinkReceiptId)).unwrap();
        detached = true;
      } catch (error) {
        log.error('Failed to detach the staged receipt:', error);
        showError("The receipt couldn't be detached. Please try again.");
      }
    }

    if (deleteReceiptId && isCurrentSession()) {
      try {
        await thunkApi.dispatch(receiptsActions.deleteReceipt(deleteReceiptId)).unwrap();
        detached = true;
      } catch (error) {
        log.error('Failed to delete the staged receipt:', error);
        showError("The receipt couldn't be deleted. Please try again.");
      }
    }

    if (!detached || !isCurrentSession()) return;
    try {
      await bindPromiseAction(thunkApi.dispatch, getTransaction)({ id: transactionId });
    } catch (error) {
      log.warn('Failed to refresh the transaction after its receipt changes:', error);
    }
  },
);
